import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useCart } from "../cart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Search, ShoppingCart, Flame, Store as StoreIcon, Zap, Plus, Minus, Trash2, Menu } from "lucide-react";
import { toast } from "sonner";

export default function Storefront() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [landing, setLanding] = useState(null);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const { getCart, addToCart, setQty, removeItem, clearCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        if (slug === "demo") {
          // fallback demo
          setStore({ store_id: "demo", slug: "demo", name: "Demo Store", tagline: "Preview mode", theme_color: "#F57224" });
        } else {
          const s = await api.get(`/stores/public/${slug}`);
          setStore(s.data);
          const p = await api.get(`/products?store_id=${s.data.store_id}`);
          setProducts(p.data);
          const l = await api.get(`/stores/${s.data.store_id}/landing`);
          setLanding(l.data);
        }
        const st = await api.get("/settings"); setSettings(st.data);
      } catch { navigate("/"); }
    })();
  }, [slug, navigate]);

  if (!store) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"/></div>;

  const cart = getCart(store.store_id);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const filtered = q ? products.filter(p => p.name.toLowerCase().includes(q.toLowerCase()) || (p.name_bn||"").includes(q)) : products;
  const flash = products.filter(p => p.flash_sale);
  const themeColor = store.theme_color || "#F57224";

  return (
    <div className="min-h-screen bg-white" style={{ "--brand": themeColor }}>
      {/* Marquee bar */}
      <div className="bg-slate-900 text-white text-xs py-1.5 overflow-hidden">
        <div className="whitespace-nowrap flex marquee">
          <span className="px-8 font-bn">🎉 আজকের ফ্ল্যাশ সেল · ফ্রি হোম ডেলিভারি (ঢাকা) · bKash/Nagad এ ৫% ক্যাশব্যাক · Cash on Delivery available · </span>
          <span className="px-8 font-bn">🎉 আজকের ফ্ল্যাশ সেল · ফ্রি হোম ডেলিভারি (ঢাকা) · bKash/Nagad এ ৫% ক্যাশব্যাক · Cash on Delivery available · </span>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/95 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={fileUrl(store.logo_url) || `https://api.dicebear.com/9.x/shapes/svg?seed=${store.slug}`} className="w-9 h-9 rounded-lg object-cover border border-slate-200" alt=""/>
            <div className="hidden sm:block">
              <div className="font-extrabold leading-tight">{store.name}</div>
              <div className="text-[10px] text-slate-500 leading-tight font-bn">{store.tagline}</div>
            </div>
          </Link>
          <div className="flex-1 relative max-w-2xl">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search... / খুঁজুন" className="pl-9 h-10 rounded-full border-slate-300" data-testid="search-input"/>
          </div>
          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetTrigger asChild>
              <button className="relative p-2 rounded-full hover:bg-slate-100" data-testid="cart-btn">
                <ShoppingCart className="w-5 h-5"/>
                {cart.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-black flex items-center justify-center">{cart.length}</span>}
              </button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md flex flex-col">
              <SheetHeader><SheetTitle>Your Cart · কার্ট</SheetTitle></SheetHeader>
              <div className="flex-1 overflow-y-auto py-4 space-y-3">
                {cart.length === 0 && <div className="text-center text-slate-400 py-16">Cart is empty</div>}
                {cart.map(i => (
                  <div key={i.product_id} className="flex gap-3 p-3 border rounded-lg" data-testid={`cart-item-${i.product_id}`}>
                    <img src={fileUrl(i.image_url) || "https://placehold.co/80"} className="w-16 h-16 rounded object-cover" alt=""/>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{i.name}</div>
                      <div className="font-mono font-black text-orange-600">৳{i.price}</div>
                      <div className="flex items-center gap-1 mt-1">
                        <button onClick={()=>setQty(store.store_id, i.product_id, i.quantity - 1)} className="w-7 h-7 border rounded"><Minus className="w-3 h-3 mx-auto"/></button>
                        <span className="w-8 text-center text-sm font-semibold">{i.quantity}</span>
                        <button onClick={()=>setQty(store.store_id, i.product_id, i.quantity + 1)} className="w-7 h-7 border rounded"><Plus className="w-3 h-3 mx-auto"/></button>
                        <button onClick={()=>removeItem(store.store_id, i.product_id)} className="ml-2 text-rose-500"><Trash2 className="w-4 h-4"/></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {cart.length > 0 && (
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between font-mono"><span>Subtotal</span><span className="font-black">৳{cartTotal}</span></div>
                  <Button onClick={() => { setCartOpen(false); navigate(`/s/${store.slug}/checkout`); }} className="w-full bg-orange-500 hover:bg-orange-600 h-12 text-base font-bold" data-testid="checkout-btn">
                    Proceed to Checkout →
                  </Button>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {slug === "demo" ? (
        <DemoView/>
      ) : (
      <main>
        {/* Hero from landing sections */}
        {landing?.sections?.filter(s => s.type === "hero").map(s => (
          <section key={s.id} className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-rose-600"/>
            <div className="absolute inset-0 grain"/>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight font-bn">{s.title}</h1>
                <p className="mt-4 text-white/90 text-lg font-bn">{s.subtitle}</p>
                <Button className="mt-6 bg-white text-orange-600 hover:bg-orange-50 rounded-full h-12 px-8 font-bold">{s.cta || "Shop Now"}</Button>
              </div>
              {s.image_url ? (
                <img src={fileUrl(s.image_url)} alt="" className="rounded-2xl shadow-2xl h-72 object-cover"/>
              ) : (
                <img src="https://images.pexels.com/photos/6956803/pexels-photo-6956803.jpeg?auto=compress&cs=tinysrgb&h=650&w=940" alt="" className="rounded-2xl shadow-2xl h-72 object-cover"/>
              )}
            </div>
          </section>
        ))}

        {/* Flash sale */}
        {flash.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-rose-500 text-white flex items-center justify-center pulse-ring"><Flame className="w-5 h-5"/></div>
              <div>
                <h2 className="text-2xl font-black">Flash Sale <span className="font-bn text-lg text-rose-500">· ফ্ল্যাশ সেল</span></h2>
                <p className="text-xs text-slate-500 font-bn">সীমিত সময়ের অফার</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {flash.map(p => <ProductCard key={p.product_id} p={p} storeId={store.store_id} addToCart={addToCart}/>)}
            </div>
          </section>
        )}

        {/* All products */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-orange-500"/>
            <h2 className="text-2xl font-black">All Products <span className="font-bn text-lg text-slate-500">· সব পণ্য</span></h2>
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-400 border border-dashed rounded-xl">No products yet</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {filtered.map(p => <ProductCard key={p.product_id} p={p} storeId={store.store_id} addToCart={addToCart}/>)}
            </div>
          )}
        </section>
      </main>
      )}

      <footer className="mt-16 bg-slate-900 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-black text-white text-lg">{store.name}</div>
            <p className="text-sm mt-2 font-bn text-slate-400">{store.tagline}</p>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Contact</div>
            <div className="text-sm">📞 {store.bkash_number || settings?.bkash_number}</div>
            <div className="text-sm">Cash on Delivery · bKash · Nagad</div>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Delivery</div>
            <div className="text-sm">Dhaka ৳{settings?.delivery_dhaka} · Outside ৳{settings?.delivery_outside}</div>
          </div>
        </div>
        <div className="text-center text-xs text-slate-500 mt-8">Powered by ShopBuilder BD</div>
      </footer>
    </div>
  );
}

function ProductCard({ p, storeId, addToCart }) {
  const off = p.compare_price > p.price ? Math.round((1 - p.price / p.compare_price) * 100) : 0;
  return (
    <div className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-orange-400 hover:shadow-lg transition-all" data-testid={`product-card-${p.product_id}`}>
      <div className="relative aspect-square bg-slate-100 overflow-hidden">
        <img src={fileUrl(p.image_url) || "https://placehold.co/400"} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
        {off > 0 && <div className="absolute top-2 left-2 bg-rose-500 text-white text-xs font-black px-2 py-0.5 rounded-full">-{off}%</div>}
        {p.flash_sale && <div className="absolute top-2 right-2 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded"><Flame className="w-3 h-3 inline"/></div>}
      </div>
      <div className="p-2.5">
        <div className="text-xs font-semibold line-clamp-2 h-8">{p.name}</div>
        {p.name_bn && <div className="text-[11px] text-slate-500 font-bn line-clamp-1">{p.name_bn}</div>}
        <div className="mt-1.5 flex items-baseline gap-1">
          <span className="text-base font-black font-mono text-orange-600">৳{p.price}</span>
          {p.compare_price > 0 && <span className="text-[10px] text-slate-400 line-through">৳{p.compare_price}</span>}
        </div>
        <button
          onClick={() => { addToCart(storeId, p); toast.success("Added to cart"); }}
          className="mt-2 w-full h-8 rounded-md bg-slate-900 hover:bg-orange-500 text-white text-xs font-bold transition-colors"
          data-testid={`add-cart-${p.product_id}`}
        >Add to Cart</button>
      </div>
    </div>
  );
}

function DemoView() {
  return (
    <div className="max-w-3xl mx-auto py-20 text-center px-4">
      <StoreIcon className="w-16 h-16 mx-auto text-orange-500"/>
      <h1 className="text-3xl font-extrabold mt-4">Demo Storefront</h1>
      <p className="text-slate-500 mt-2 font-bn">এটি একটি প্রিভিউ। আসল স্টোর দেখতে ড্যাশবোর্ড থেকে স্টোর তৈরি করুন।</p>
      <Link to="/dashboard"><Button className="mt-6 bg-orange-500 hover:bg-orange-600 rounded-full">Go to Dashboard</Button></Link>
    </div>
  );
}
